import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Linking } from "react-native";

import { events } from "@constants/tracking";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import i18n from "@helpers/lib/i18n";
import { useAppStore } from "@hooks/useAppStore";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import useTutorialCache from "@hooks/useTutorialCache";
import TTSContainer from "@memorang/features/src/TTS/TTSContainer";
import { isSafeUrl, titleCase } from "@memorang/helpers/common";
import { getAiAssistantIconUri } from "@memorang/helpers/constants";
import { isAnswerSkipped } from "@memorang/helpers/item";
import { isWeb } from "@memorang/helpers/platform";
import type { Answer, UnifiedCitation } from "@memorang/types";
import type { PostAnswerPanelValue } from "@memorang/ui/components/StudySession/PostAnswerPanel";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { useDevice } from "@memorang/ui/useDevice";
import { trackCustomEvent } from "analytics";
import { useRouter } from "expo-router";
import type { TFunction } from "i18next";

import { AIFeedbackMenu } from "../components/AIFeedbackMenu";
import { isPteFeedbackAgentItem } from "../constants/pte/feedback";
import {
	buildPostAnswerStats,
	buildSubskillItems,
	calculateXp,
} from "../containers/StudySessionFeedbackContainer/helpers";
import { resolveAiScore } from "../helpers/answer-score";
import useAIFeedbackStore from "./useAIFeedbackStore";
import { useAilaStore } from "./useAilaStore";
import { usePostAnswerAiSync } from "./usePostAnswerAiSync";
import { usePostAnswerPrompt } from "./usePostAnswerPrompt";
import { usePostAnswerState } from "./usePostAnswerState";

type UsePostAnswerAppletValueParams = {
	isSplitLayout?: boolean;
	suggestCorrectionToggle: () => void;
	openAiDisclaimer: () => void;
};

const resolveSkippedFeedbackText = ({
	isAiFeedbackText,
	currentAnswer,
	currentUserResponse,
	t,
}: {
	isAiFeedbackText: boolean;
	currentAnswer?: Answer;
	currentUserResponse: unknown;
	t: TFunction;
}): string | undefined => {
	if (!isAiFeedbackText) {
		return undefined;
	}
	const responseText =
		typeof currentUserResponse === "string" ? currentUserResponse : undefined;
	return isAnswerSkipped(currentAnswer, responseText)
		? t("session.explanation.skippedFeedbackNotice")
		: undefined;
};

export const usePostAnswerAppletValue = ({
	isSplitLayout,
	openAiDisclaimer,
	suggestCorrectionToggle,
}: UsePostAnswerAppletValueParams) => {
	const { t } = useTranslation();
	const theme = useAppTheme();
	const router = useRouter();
	const { isDesktopWeb } = useDevice();
	const ailaEnabled = useAppStore((store) => store.sessionView?.ai?.aila);

	const { tutorialData } = useTutorialCache();

	const aiDisclaimer = useAppStore(
		(state) => state.app.aiAssistant?.disclaimer,
	);
	const masteryLevels = useAppStore((state) => state.app.masteryLevels);

	const shouldShowAiDisclaimer = Boolean(
		aiDisclaimer?.enabled && !tutorialData?.hasSeenAiDisclaimer,
	);

	const { handleSuggestCorrectionNavigation } = useCustomNavigation();
	const {
		currentExamContext: { examName },
	} = useExamContext();

	const state = usePostAnswerState();

	const isPteFeedback = isPteFeedbackAgentItem(
		state.currentItem.metadata?.type,
	);

	const updateIsAssistantVisible = useAilaStore(
		(state) => state.updateShowDrawer,
	);
	const updateItemId = useAilaStore((state) => state.updateItemId);
	const updateUserPrompt = useAilaStore((state) => state.updateUserPrompt);
	const updateCacheIdentity = useAilaStore(
		(state) => state.updateCacheIdentity,
	);
	const updateIsItemAnswered = useAilaStore(
		(state) => state.updateIsItemAnswered,
	);
	const updateOriginalQuestionData = useAilaStore(
		(state) => state.updateOriginalQuestionData,
	);
	const updateSelectedQuery = useAilaStore(
		(state) => state.updateSelectedQuery,
	);
	const showDrawer = useAilaStore((state) => state.showDrawer);
	const updateDrawer = useAilaStore((state) => state.updateShowDrawer);

	const prompt = usePostAnswerPrompt({
		aiFeedback: state.aiFeedback,
		aiResponse: state.aiResponse,
		aiTranscription: state.aiTranscription,
		currentAnswer: state.currentAnswer,
		currentItem: state.currentItem,
		currentUserResponse: state.currentUserResponse,
		examName,
		isAiItemType: state.isAiItemType,
		isAssessorItemType: state.isAssessorItemType,
		isDesktopWeb,
		isStreaming: state.isStreaming,
		itemId: state.itemId,
		openAiDisclaimer,
		shouldRenderPostAnswer: state.shouldRenderPostAnswer,
		shouldShowAiDisclaimer,
		topics: state.topics,
		updateIsAssistantVisible,
		updateSelectedQuery,
	});

	usePostAnswerAiSync({
		cacheIdentity: prompt.cacheIdentity,
		isDesktopWeb: Boolean(isDesktopWeb),
		itemId: state.itemId,
		originalQuestionData: prompt.originalQuestionData,
		shouldRenderPostAnswer: state.shouldRenderPostAnswer,
		updateIsAssistantVisible,
		updateIsItemAnswered,
		updateItemId,
		updateOriginalQuestionData,
		updateCacheIdentity,
		updateSelectedQuery,
		updateUserPrompt,
		userPrompt: prompt.userPrompt,
	});

	const [citationsPageNumber, setCitationsPageNumber] = useState<number | null>(
		null,
	);

	const recordingDurationMillis = useAIFeedbackStore(
		(store) => store.recordingDurations[state.itemId],
	);

	const xpEarned = calculateXp(state.numAttempts, state.isHintShown);
	const aiScore = resolveAiScore({
		item: state.currentItem,
		aiResponse: state.aiResponse,
	});

	const stats = buildPostAnswerStats({
		aiScore,
		attemptsStatValue: state.attemptsStatValue,
		confidence:
			isPteFeedback || state.isSummativeTest ? undefined : state.confidence,
		correct: state.correct,
		currentAnswer: state.currentAnswer,
		currentItem: state.currentItem,
		currentUserResponse:
			typeof state.currentUserResponse === "string"
				? state.currentUserResponse
				: undefined,
		isAssessorItemType: state.isAssessorItemType,
		maximumScore: state.maximumScore,
		numResponses: state.numResponses,
		numberOfWords: state.numberOfWords,
		recordingDurationSeconds: recordingDurationMillis
			? Math.round(recordingDurationMillis / 1000)
			: undefined,
		showNumAttempts: state.showNumAttempts,
		showXpEarned: state.showXpEarned,
		t,
		theme,
		timeTaken: state.currentAnswer?.timeTaken ?? 0,
		xpEarned,
	});

	const tags = state.currentItem.tags?.map(({ label, type: tagType, title }) =>
		tagType ? `${titleCase(tagType)}: ${label || title}` : label || title || "",
	);

	const subskillAssessments = buildSubskillItems({
		aiResponse: state.aiResponse,
		currentItem: state.currentItem,
		masteryLevels,
		maximumScore: state.maximumScore,
		t,
		theme,
	});

	const handleSuggestCorrectionClick = useCallback(() => {
		handleSuggestCorrectionNavigation(
			suggestCorrectionToggle,
			`${state.currentItem.id!}`,
		);
	}, [
		handleSuggestCorrectionNavigation,
		state.currentItem.id,
		suggestCorrectionToggle,
	]);

	// Feedback-agent items stream AI feedback text, so gate TTS/menu like AI items
	// (no prefetch, disabled while streaming) even though WFDL is not isAiSubmittable.
	const isAiFeedbackText = state.isAiItemType || isPteFeedback;

	const emptyFeedbackText = resolveSkippedFeedbackText({
		isAiFeedbackText,
		currentAnswer: state.currentAnswer,
		currentUserResponse: state.currentUserResponse,
		t,
	});

	const renderTtsContainer = useCallback(
		(explanation?: string) =>
			explanation ? (
				<TTSContainer
					key={`${state.itemId}`}
					itemId={`${state.itemId}`}
					explanation={explanation}
					eventName={events.ttsClicked}
					trackCustomEvent={trackCustomEvent}
					size={18}
					allowPrefetch={!isAiFeedbackText}
					disabled={isAiFeedbackText && state.isStreaming}
				/>
			) : null,
		[isAiFeedbackText, state.isStreaming, state.itemId],
	);

	const renderFeedbackMenu = useCallback(
		() =>
			isAiFeedbackText ? (
				<AIFeedbackMenu
					itemId={state.itemId}
					featureName="AI Feedback"
					disabled={state.isStreaming}
				/>
			) : null,
		[isAiFeedbackText, state.isStreaming, state.itemId],
	);

	const handleOnPressReference = useCallback(
		(reference: UnifiedCitation) => {
			if (reference.url) {
				if (!isSafeUrl(reference.url)) {
					return;
				}
				if (isWeb) {
					window.open(reference.url, "_blank", "noopener,noreferrer");
				} else {
					Linking.openURL(reference.url).catch(() => {
						/* ignore open failures */
					});
				}
				return;
			}
			if (
				reference.page != null &&
				Number.isInteger(reference.page) &&
				reference.page > 0
			) {
				if (isWeb) {
					setCitationsPageNumber(reference.page);
					return;
				}
				updateDrawer(true);
				router.push(`/citation/${reference.page}`);
			}
		},
		[router, updateDrawer],
	);

	const handleExpandCollapse = useCallback(() => {
		updateIsAssistantVisible(!showDrawer);
	}, [showDrawer, updateIsAssistantVisible]);

	const handleTrackSheetOpened = useCallback(() => {
		trackCustomEvent({
			eventName: events.explanationSheetOpened,
			correct: String(state.correct),
			confidence: state.confidence,
			timeTaken: state.currentAnswer?.timeTaken,
		});
	}, [state.correct, state.confidence, state.currentAnswer?.timeTaken]);

	const postAnswerPanelValue: PostAnswerPanelValue = {
		aiAssistant: {
			icon: state.aiAssistant
				? getAiAssistantIconUri(state.aiAssistant, theme.dark)
				: "",
			alias: state.aiAssistant?.alias ?? state.aiAssistantTitle,
			onPress: openAiDisclaimer,
		},
		handleExpandCollapse,
		handleNavigateToReference: handleOnPressReference,
		answerState: {
			itemAnswered: state.shouldRenderPostAnswer,
			questionAnswered: state.shouldRenderPostAnswer,
			itemIndex: state.currentItemIndex,
			streaming: state.isStreaming,
		},
		explanation: {
			title: t("session.explanation.title"),
			appFont: state.appFont,
			fontSize: state.fontSize,
			language: i18n.language,
			currentAiFeedback: state.aiFeedback,
			persistedAiFeedback:
				state.currentItem.answer?.aiResponse?.explanation ??
				state.currentItem.answer?.aiResponse?.feedback,
			emptyFeedbackText,
			renderFeedbackMenu,
			renderTtsContainer,
			streaming: state.isStreaming,
			visible: true,
			forceDynamic: isPteFeedback,
		},
		followUps: {
			visible: prompt.shouldShowFollowUps && Boolean(ailaEnabled),
			loading:
				(isAiFeedbackText && state.isStreaming) ||
				prompt.isRecommendedQueriesLoading,
			queries: prompt.recommendedQueries,
			onSelectQuery: prompt.handleSelectFollowUp,
			title: t("session.followUps"),
			showCustomResponse: true,
		},
		headerLabel: t("session.explanation.feedback"),
		item: {
			id: state.currentItem.id!,
			responseType: state.currentItem.response.type,
			itemType: state.currentItem.metadata?.type,
			toeicArtifactType: state.currentItem.metadata?.toeic_artifact_type,
			choices: state.choices,
			feedback: state.currentItem.feedback,
			citations: state.combinedCitations,
			showCitations: state.showCitations,
			tags,
		},
		layout: { isSplitLayout, isSummativeTest: state.isSummativeTest },
		score: {
			stats,
			maximumScore: state.maximumScore,
			overallScoreLabel: t("session.overall"),
			score: aiScore,
			subskillAssessments,
			showAiHeader: !isPteFeedback,
		},
		suggestCorrection: state.isSummativeTest
			? undefined
			: {
					cta: t("session.suggestCorrection"),
					handleClick: handleSuggestCorrectionClick,
				},
		showTextBasedCitationOnly: state.showTextBasedCitationOnly,
		handleTrackSheetOpened: handleTrackSheetOpened,
	};

	return {
		aiAssistantTitle: state.aiAssistantTitle,
		aiDisclaimer,
		citationsPageNumber,
		currentItem: state.currentItem,
		handleCloseCitationDialog: () => setCitationsPageNumber(null),
		postAnswerPanelValue,
		shouldRenderPostAnswer: state.shouldRenderPostAnswer,
		shouldShowAiDisclaimer,
		isSummativeTest: state.isSummativeTest,
	};
};
